import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

const SIZE_SYSTEMS = ['EUR', 'US M', 'US W', 'UK'];
const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
  'CH','MC','GF','GP','MQ','RE','YT','BL','MF','PM','NC','PF','WF',
]);
const UK_COUNTRY_CODES = new Set(['GB', 'UK']);
const SIZE_SYSTEM_ORDERS = {
  default: ['US M', 'US W', 'EUR', 'UK'],
  eu: ['EUR', 'US M', 'US W', 'UK'],
  uk: ['UK', 'US M', 'US W', 'EUR'],
};

const SIZE_CONVERSION_MAPS = {
  NIKE_MIND: {
    '36': { 'US M': '4', 'US W': '5', UK: '3.5' },
    '37.5': { 'US M': '5', 'US W': '6', UK: '4.5' },
    '38.5': { 'US M': '6', 'US W': '7', UK: '5.5' },
    '40': { 'US M': '7', 'US W': '8', UK: '6' },
    '41': { 'US M': '8', 'US W': '9', UK: '7' },
    '42.5': { 'US M': '9', 'US W': '10', UK: '8' },
    '44': { 'US M': '10', 'US W': '11', UK: '9' },
    '45': { 'US M': '11', 'US W': '12', UK: '10' },
    '46': { 'US M': '12', 'US W': '13', UK: '11' },
    '47.5': { 'US M': '13', 'US W': '14', UK: '12' },
    '48.5': { 'US M': '14', 'US W': '15', UK: '13' },
  },
  NIKE: {
    '35.5': { 'US M': '3.5', 'US W': '5', UK: '2.5' },
    '36': { 'US M': '4', 'US W': '5.5', UK: '3' },
    '36.5': { 'US M': '4.5', 'US W': '6', UK: '3.5' },
    '37.5': { 'US M': '5', 'US W': '6.5', UK: '4' },
    '38': { 'US M': '5.5', 'US W': '7', UK: '4.5' },
    '38.5': { 'US M': '6', 'US W': '7.5', UK: '5' },
    '39': { 'US M': '6.5', 'US W': '8', UK: '5.5' },
    '40': { 'US M': '7', 'US W': '8.5', UK: '6' },
    '40.5': { 'US M': '7.5', 'US W': '9', UK: '6.5' },
    '41': { 'US M': '8', 'US W': '9.5', UK: '7' },
    '42': { 'US M': '8.5', 'US W': '10', UK: '7.5' },
    '42.5': { 'US M': '9', 'US W': '10.5', UK: '8' },
    '43': { 'US M': '9.5', 'US W': '11', UK: '8.5' },
    '44': { 'US M': '10', 'US W': '11.5', UK: '9' },
    '44.5': { 'US M': '10.5', 'US W': '12', UK: '9.5' },
    '45': { 'US M': '11', 'US W': '12.5', UK: '10' },
    '45.5': { 'US M': '11.5', 'US W': '13', UK: '10.5' },
    '46': { 'US M': '12', 'US W': '13.5', UK: '11' },
    '47': { 'US M': '12.5', 'US W': '14', UK: '11.5' },
    '47.5': { 'US M': '13', 'US W': '14.5', UK: '12' },
  },
  ADIDAS: {
    '36': { 'US M': '4', 'US W': '5', UK: '3.5' },
    '36 2/3': { 'US M': '4.5', 'US W': '5.5', UK: '4' },
    '37 1/3': { 'US M': '5', 'US W': '6', UK: '4.5' },
    '38': { 'US M': '5.5', 'US W': '6.5', UK: '5' },
    '38 2/3': { 'US M': '6', 'US W': '7', UK: '5.5' },
    '39 1/3': { 'US M': '6.5', 'US W': '7.5', UK: '6' },
    '40': { 'US M': '7', 'US W': '8', UK: '6.5' },
    '40 2/3': { 'US M': '7.5', 'US W': '8.5', UK: '7' },
    '41 1/3': { 'US M': '8', 'US W': '9', UK: '7.5' },
    '42': { 'US M': '8.5', 'US W': '9.5', UK: '8' },
    '42 2/3': { 'US M': '9', 'US W': '10', UK: '8.5' },
    '43 1/3': { 'US M': '9.5', 'US W': '10.5', UK: '9' },
    '44': { 'US M': '10', 'US W': '11', UK: '9.5' },
    '44 2/3': { 'US M': '10.5', 'US W': '11.5', UK: '10' },
    '45 1/3': { 'US M': '11', 'US W': '12', UK: '10.5' },
    '46': { 'US M': '11.5', 'US W': '12.5', UK: '11' },
  },
};

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#sizeSystemChanged);
    document.addEventListener('change', this.#localizationChanged, true);
    document.addEventListener('click', this.#localizationChanged, true);
    document.addEventListener('submit', this.#localizationChanged, true);
    this.#resizeObserver.observe(this);
    this.#syncSizeConversion();
    requestAnimationFrame(() => this.#syncSizeConversion());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    document.removeEventListener('change', this.#localizationChanged, true);
    document.removeEventListener('click', this.#localizationChanged, true);
    document.removeEventListener('submit', this.#localizationChanged, true);
  }

  /** @param {Event} event */
  #localizationChanged = (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const form = target?.closest('form.localization-form');
    const countryItem = target?.closest('.localization-form__list-item[data-value]');
    if (!form && !countryItem) return;

    const country = countryItem instanceof HTMLElement
      ? countryItem.dataset.value
      : form?.querySelector('input[name="country_code"]')?.value;
    const language = form?.querySelector('[name="language_code"]')?.value;

    window.setTimeout(() => {
      if (country) this.dataset.marketCountry = country;
      if (language) this.dataset.marketLanguage = language;
      delete this.dataset.sizeSystem;
      this.#syncSizeConversion();
    }, 0);
  };

  /** @param {Event} event */
  #sizeSystemChanged = (event) => {
    const button = event.target instanceof HTMLElement ? event.target.closest('[data-size-system]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    this.dataset.sizeSystem = button.dataset.sizeSystem || 'EUR';
    this.#syncSizeConversion();
  };

  #syncSizeConversion() {
    const sizeFieldset = this.#sizeFieldset;
    if (!sizeFieldset) {
      this.#clearSizeProperties();
      return;
    }

    this.#syncMarketDataFromPage();
    const sizeOrder = this.#marketSizeOrder();
    const activeSystem = sizeOrder.includes(this.dataset.sizeSystem || '') ? this.dataset.sizeSystem : this.#defaultSizeSystem();
    this.dataset.sizeSystem = activeSystem;
    this.#ensureSizeTabs(sizeFieldset, activeSystem);

    sizeFieldset.querySelectorAll('input').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const euSize = this.#euSize(input.value);
      if (!euSize) return;
      const text = input.closest('label')?.querySelector('[data-key="variant-option-text"]');
      if (text) text.textContent = activeSystem === 'EUR' ? input.value : this.#sizeConversions[euSize]?.[activeSystem] || input.value;
    });

    this.#syncSizeLegend(sizeFieldset, activeSystem);
    this.#syncSizeProperties(activeSystem);
  }

  get #sizeFieldset() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    return fieldsets.find((fieldset) =>
      Array.from(fieldset.querySelectorAll('input')).some((input) => this.#euSize(input.value))
    );
  }

  /**
   * @param {HTMLFieldSetElement} fieldset
   * @param {string} activeSystem
   */
  #ensureSizeTabs(fieldset, activeSystem) {
    let tabs = this.querySelector('.size-system-tabs');
    if (!tabs) {
      tabs = document.createElement('div');
      tabs.className = 'size-system-tabs';
      tabs.setAttribute('role', 'tablist');
      fieldset.insertAdjacentElement('beforebegin', tabs);
    }

    const label = this.#sizeSystemLabel();
    const sizeOrder = this.#marketSizeOrder();
    tabs.setAttribute('aria-label', label);
    tabs.setAttribute('translate', 'no');
    tabs.classList.add('notranslate');
    tabs.innerHTML = '';
    sizeOrder.forEach((system) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-system-tabs__button notranslate';
      button.setAttribute('role', 'tab');
      button.setAttribute('translate', 'no');
      button.setAttribute('lang', 'en');
      button.setAttribute('data-size-system', system);
      button.setAttribute('aria-selected', system === activeSystem ? 'true' : 'false');
      button.textContent = system;
      tabs.append(button);
    });
  }

  /**
   * @param {HTMLFieldSetElement} fieldset
   * @param {string} activeSystem
   */
  #syncSizeLegend(fieldset, activeSystem) {
    const legend = fieldset.querySelector('legend');
    if (!legend) return;
    const textNode = Array.from(legend.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (textNode) textNode.textContent = '\n              ' + this.#sizeSystemLabel() + '\n              ';
    const selectedValue = legend.querySelector('[data-size-selected-value]');
    const selectedSize = fieldset.querySelector('input:checked');
    if (!(selectedValue instanceof HTMLElement) || !(selectedSize instanceof HTMLInputElement)) return;

    const euSize = this.#euSize(selectedSize.value);
    const displayedSize = activeSystem === 'EUR' ? euSize : this.#sizeConversions[euSize]?.[activeSystem];
    selectedValue.textContent = displayedSize || selectedSize.value;
  }

  #syncMarketDataFromPage() {
    const country = this.dataset.marketCountry || document.querySelector('form.localization-form input[name="country_code"]')?.value || window.Shopify?.country;
    const language = this.dataset.marketLanguage || document.querySelector('form.localization-form [name="language_code"]')?.value || document.documentElement.lang;
    if (country) this.dataset.marketCountry = country;
    if (language) this.dataset.marketLanguage = language;
  }

  #marketKey() {
    return this.#marketCountry() + ':' + this.#marketLanguage();
  }

  #normalizeSize(value) {
    return value.trim().replace(",", ".").replace(/^EU\s*/i, "").replace(/\s+/g, " ");
  }

  #marketCountry() {
    return (this.dataset.marketCountry || window.Shopify?.country || '').toUpperCase();
  }

  #marketCurrency() {
    return (this.dataset.marketCurrency || '').toUpperCase();
  }

  #hasCountry() {
    return this.#marketCountry() !== '';
  }

  #isUkMarket() {
    const country = this.#marketCountry();
    return country ? UK_COUNTRY_CODES.has(country) : false;
  }

  #isEuMarket() {
    const country = this.#marketCountry();
    return country ? EU_COUNTRIES.has(country) : false;
  }

  #marketLanguage() {
    return (this.dataset.marketLanguage || document.documentElement.lang || '').toLowerCase();
  }

  #isNikeMindProduct() {
    const productText = `${this.dataset.productTitle || ''} ${this.dataset.productUrl || ''}`.toLowerCase();
    return productText.includes('mind');
  }

  #sizeSystemLabel() {
    return this.#marketLanguage().startsWith('fr') && this.#marketCountry() === 'FR' ? 'Taille' : 'Size';
  }

  #marketSizeOrder() {
    if (this.#isUkMarket()) return SIZE_SYSTEM_ORDERS.uk;
    if (this.#isEuMarket()) return SIZE_SYSTEM_ORDERS.eu;
    return SIZE_SYSTEM_ORDERS.default;
  }

  #defaultSizeSystem() {
    return this.#marketSizeOrder()[0];
  }

  get #sizeConversions() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const hasThirdSizes = fieldsets.some((fieldset) =>
      Array.from(fieldset.querySelectorAll("input")).some((input) => {
        const size = this.#normalizeSize(input.value);
        return size.includes("1/3") || size.includes("2/3");
      })
    );

    if (this.#isNikeMindProduct()) return SIZE_CONVERSION_MAPS.NIKE_MIND;
    return hasThirdSizes ? SIZE_CONVERSION_MAPS.ADIDAS : SIZE_CONVERSION_MAPS.NIKE;
  }
  /** @param {string} value */
  #euSize(value) {
    const normalized = this.#normalizeSize(value);
    return this.#sizeConversions[normalized] ? normalized : '';
  }

  /** @param {string} activeSystem */
  #syncSizeProperties(activeSystem) {
    const selectedSize = this.#sizeFieldset?.querySelector('input:checked');
    if (!(selectedSize instanceof HTMLInputElement)) {
      this.#clearSizeProperties();
      return;
    }

    const euSize = this.#euSize(selectedSize.value);
    if (!euSize) {
      this.#clearSizeProperties();
      return;
    }

    const displayedSize = activeSystem === 'EUR' ? euSize : this.#sizeConversions[euSize]?.[activeSystem];
    const form = this.#productForm;
    if (!displayedSize || !(form instanceof HTMLFormElement)) {
      this.#clearSizeProperties();
      return;
    }

    this.#setProperty(form, 'Displayed Size', displayedSize);
    this.#setProperty(form, 'Size System', activeSystem);
    this.#setProperty(form, 'Base EU Size', euSize);
  }

  get #productForm() {
    return this.closest('.shopify-section')?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] form`
    );
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #setProperty(form, name, value) {
    const input = form.querySelector(`input[name="properties[${name}]"]`);
    if (input instanceof HTMLInputElement) {
      input.value = value;
      input.disabled = false;
    }
  }

  #clearSizeProperties() {
    const form = this.#productForm;
    if (!(form instanceof HTMLFormElement)) return;

    ['Displayed Size', 'Size System', 'Base EU Size'].forEach((name) => {
      const input = form.querySelector(`input[name="properties[${name}]"]`);
      if (input instanceof HTMLInputElement) {
        input.value = '';
        input.disabled = true;
      }
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
      this.#syncSizeConversion();
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    const previousMarketKey = this.#marketKey();
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      const newProductTitle = newVariantPickerSource.dataset.productTitle;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
      this.dataset.productTitle = newProductTitle || this.dataset.productTitle;
      this.dataset.marketCountry = newVariantPickerSource.dataset.marketCountry || this.dataset.marketCountry;
      this.dataset.marketCurrency = newVariantPickerSource.dataset.marketCurrency || this.dataset.marketCurrency;
      this.dataset.marketLanguage = newVariantPickerSource.dataset.marketLanguage || this.dataset.marketLanguage;
    }

    const activeSizeSystem = this.dataset.sizeSystem;

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    if (activeSizeSystem && previousMarketKey === this.#marketKey()) this.dataset.sizeSystem = activeSizeSystem;
    else delete this.dataset.sizeSystem;
    this.#syncSizeConversion();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
